import { apiGet, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface AuditLogEntry {
  id: number;
  created_ts: string;
  event: string;
  success: boolean;
  actor: { id: number; email: string; name: string } | null;
  target_user: { id: number; email: string; name: string } | null;
  ip: string | null;
  user_agent: string | null;
  payload: Record<string, unknown> | null;
  geo_country: string | null;
  geo_city: string | null;
  geo_location: string | null;
  /** `environment`: the Tenant cell marks a sandbox; null for an operator that no longer exists. */
  tenant: {
    id: number;
    code: string | null;
    name: string | null;
    environment?: TourOperatorEnvironment | null;
  } | null;
  linked_object: { content_type: string; object_id: number } | null;
  /** Background-task run that produced this row — groups the launch row with
   *  every per-record row the task wrote. Null for direct (request-side) actions. */
  task_id: string | null;
}

export interface AuditFilters {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  limit?: number;
  offset?: number;
  event?: string;
  success?: boolean;
  actor?: number;
  target_user?: number;
  /** One id or a comma list of ids (D1). */
  tour_operator?: TenantIdsParam;
  event_contains?: string;
  actor_email?: string;
  target_email?: string;
  ip?: string;
  country?: string;
  city?: string;
  location?: string;
  geo?: string;
  task_id?: string;
  /** One onboarding invite's rows (payload.invite_id: signup.* and onboarding_invite.*). */
  invite?: number;
  search?: string;
  ordering?: string;
  created_after?: string;
  created_before?: string;
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default — that omission is what keeps a cold first load free. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

export interface AuditListResponse extends PaginatedResponse<AuditLogEntry> {
  /** False when the caller lacks console_audit_view_all — results are then
   *  scoped to their own actions server-side. */
  can_view_all?: boolean;
}

function toQuery(f: AuditFilters): string {
  const usp = new URLSearchParams();
  if (f.limit) usp.set('limit', String(f.limit));
  if (f.offset) usp.set('offset', String(f.offset));
  if (f.event) usp.set('event', f.event);
  if (f.count_mode) usp.set('count_mode', f.count_mode);
  if (f.count_only) usp.set('count_only', '1');
  if (f.success !== undefined) usp.set('success', String(f.success));
  if (f.actor) usp.set('actor', String(f.actor));
  if (f.target_user) usp.set('target_user', String(f.target_user));
  if (f.tour_operator) usp.set('tour_operator', String(f.tour_operator));
  if (f.event_contains) usp.set('event_contains', f.event_contains);
  if (f.actor_email) usp.set('actor_email', f.actor_email);
  if (f.target_email) usp.set('target_email', f.target_email);
  if (f.ip) usp.set('ip', f.ip);
  if (f.country) usp.set('country', f.country);
  if (f.city) usp.set('city', f.city);
  if (f.location) usp.set('location', f.location);
  if (f.geo) usp.set('geo', f.geo);
  if (f.task_id) usp.set('task_id', f.task_id);
  if (f.invite) usp.set('invite', String(f.invite));
  if (f.search) usp.set('search', f.search);
  if (f.ordering) usp.set('ordering', f.ordering);
  if (f.timeout) usp.set('timeout', f.timeout);
  if (f.created_after) usp.set('created_after', f.created_after);
  if (f.created_before) usp.set('created_before', f.created_before);
  return usp.toString();
}

export async function list(filters: AuditFilters = {}): Promise<AuditListResponse> {
  const qs = toQuery(filters);
  return apiGet(`/audit/${qs ? `?${qs}` : ''}`, extendedListConfig(filters.timeout));
}

/** Total only, for the grid's on-demand row count (computed in Celery). */
export async function count(filters: AuditFilters = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...filters, count_only: true, count_mode: 'async' });
  return apiGet(`/audit/?${qs}`);
}

export async function retrieve(id: number): Promise<AuditLogEntry> {
  return apiGet(`/audit/${id}/`);
}

/** django-reversion change linked from an audit entry
 *  (linked_object.content_type === "reversion.revision"). */
export interface RevisionVersion {
  id: number;
  content_type: string;
  object_id: string;
  object_repr: string;
  /** Stored field values (secret-looking names redacted); null when withheld. */
  fields: Record<string, unknown> | null;
  /** Field-level diff vs the previous version; null for the first version. */
  changes: Record<string, { old: unknown; new: unknown }> | null;
  note?: string;
}

export interface RevisionDetail {
  id: number;
  date_created: string;
  user: { id: number; email: string } | null;
  comment: string;
  versions: RevisionVersion[];
  versions_truncated?: boolean;
}

export async function retrieveRevision(id: number): Promise<RevisionDetail> {
  return apiGet(`/revisions/${id}/`);
}
